// src/utils/logger.js

const LOG_LEVELS = {
  ERROR: 'error',
  WARN: 'warn',
  INFO: 'info',
  DEBUG: 'debug'
};

class BrowserLogger {
  constructor() {
    this.logs = [];
    this.maxLogs = 100;
    this.isDevelopment = process.env.NODE_ENV !== 'production';
    this.isProduction = process.env.NODE_ENV === 'production';
  }

  _log(level, message, meta = {}) {
    const logEntry = {
      timestamp: new Date().toISOString(),
      level,
      message: typeof message === 'object' ? JSON.stringify(message) : message,
      meta: typeof meta === 'object' ? meta : { details: meta },
      environment: process.env.NODE_ENV
    };

    // Store log in memory
    this.logs.push(logEntry);

    // Trim logs if exceeding max
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    // Only output to console in development
    if (this.isDevelopment) {
      this._consoleOutput(level, logEntry);
    }

    // In production, you could send to a logging service
    if (this.isProduction) {
      this._sendToLoggingService(logEntry);
    }
  }

  _consoleOutput(level, logEntry) {
    const formattedMessage = this._formatLogMessage(logEntry);

    switch (level) {
      case LOG_LEVELS.ERROR:
        // eslint-disable-next-line no-console
        console.error(formattedMessage);
        break;
      case LOG_LEVELS.WARN:
        // eslint-disable-next-line no-console
        console.warn(formattedMessage);
        break;
      case LOG_LEVELS.INFO:
        // eslint-disable-next-line no-console
        console.info(formattedMessage);
        break;
      case LOG_LEVELS.DEBUG:
        // eslint-disable-next-line no-console
        console.debug(formattedMessage);
        break;
      default:
        // eslint-disable-next-line no-console
        console.log(formattedMessage);
    }
  }

  _sendToLoggingService(logEntry) {
    // In production, send critical logs to your backend
    if (logEntry.level === LOG_LEVELS.ERROR || logEntry.level === LOG_LEVELS.WARN) {
      // Send to backend API (implement this later)
      // For now, just store in memory

      // Example implementation:
      // fetch('/api/logs/frontend', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(logEntry)
      // }).catch(() => {
      //   // Fail silently to avoid infinite loops
      // });
    }
  }

  _formatLogMessage(logEntry) {
    return `[${logEntry.timestamp}] ${logEntry.level.toUpperCase()}: ${logEntry.message}${Object.keys(logEntry.meta).length > 0
      ? ` - ${JSON.stringify(logEntry.meta)}`
      : ''
      }`;
  }

  error(message, meta = {}) {
    this._log(LOG_LEVELS.ERROR, message, meta);
  }

  warn(message, meta = {}) {
    this._log(LOG_LEVELS.WARN, message, meta);
  }

  info(message, meta = {}) {
    this._log(LOG_LEVELS.INFO, message, meta);
  }

  debug(message, meta = {}) {
    this._log(LOG_LEVELS.DEBUG, message, meta);
  }

  logError(context, error) {
    this.error(`[${context}] ${error.message}`, {
      stack: error.stack,
      name: error.name
    });
  }

  // Export logs for debugging or sending to support
  exportLogs() {
    return this.logs;
  }

  // Clear logs
  clearLogs() {
    this.logs = [];
  }

  // Get recent logs (useful for debugging)
  getRecentLogs(count = 10) {
    return this.logs.slice(-count);
  }
}

// Export a singleton instance - ONLY ONE DECLARATION
const logger = new BrowserLogger();

export default logger;